import React, { forwardRef, useState } from "react";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * ToggleUI — singurul comutator / singura bifă a aplicației. Înlocuiește UIToggle
 * (comutatorul din plăcuțele de profil, Adaugă slot, Înscriere, Setări, „Tot anul”),
 * bifa desenată sign__checkbox (Creare cont, Înscriere), cercul cu punct
 * examPermissionPopup__checkbox (Acces examen) și bifele native (Sectoare, Șterge
 * rezervările, Caută loc liber, Programul instructorului). Stilurile: scss/UI/_toggleUI.scss.
 *
 * look: switch (implicit: pista 60×30, degetul 32×24, pornit = accent; <button role="switch">) |
 *       checkbox (bifa desenată: cerc de 20px cu contur 2px accent, bifată = accent plin
 *       cu bifa; un <input type="checkbox"> real, ascuns doar vizual, merge cu Tab și Space)
 * checked: pornit / bifat (fără el: defaultChecked, starea o ține elementul)
 * onChange(next, event): starea nouă (true / false)
 * disabled: nu se mai apasă, pe jumătate stins
 * label: textul de lângă (text sau JSX, cu linkuri), clicabil ca un <label>;
 *        la switch în stânga, cu comutatorul în dreapta (space-between, ca plăcuța
 *        cu comutator); la checkbox în dreapta bifei
 * aria-label: eticheta pentru cititoarele de ecran când nu e label (obligatorie atunci)
 * className / style: pe element. Restul (name, required, id, title…) merge pe
 * <button> (switch) sau pe <input> (checkbox); ref-ul e al lor.
 */
const ToggleUI = forwardRef(function ToggleUI(
   {
      look = "switch",
      checked,
      defaultChecked = false,
      onChange,
      disabled = false,
      label,
      className = "",
      style,
      ...rest
   },
   ref,
) {
   const [inner, setInner] = useState(Boolean(defaultChecked));
   const controlled = checked !== undefined;
   const on = controlled ? checked === true : inner;
   const hasLabel = label != null && label !== false && label !== "";

   const change = (next, event) => {
      if (disabled) return;
      if (!controlled) setInner(next);
      onChange?.(next, event);
   };

   const classes = cx(
      "toggleUI",
      `toggleUI--${look}`,
      hasLabel && "toggleUI--labeled",
      on && "is-on",
      disabled && "is-disabled",
      className,
   );
   const text = hasLabel ? <span className="toggleUI__label">{label}</span> : null;

   if (look === "checkbox") {
      return (
         <label className={classes} style={style}>
            <input
               {...rest}
               ref={ref}
               type="checkbox"
               className="toggleUI__input"
               checked={on}
               disabled={disabled}
               onChange={(e) => change(e.target.checked, e)}
            />
            <span className="toggleUI__box" aria-hidden="true">
               <svg className="toggleUI__tick" viewBox="0 0 20 20" focusable="false">
                  <polyline points="4,11 8,15 16,6" />
               </svg>
            </span>
            {text}
         </label>
      );
   }

   const Root = hasLabel ? "label" : "span";
   return (
      <Root className={classes} style={style}>
         {text}
         <button
            {...rest}
            ref={ref}
            type="button"
            role="switch"
            aria-checked={on}
            className="toggleUI__switch"
            disabled={disabled}
            onClick={(e) => change(!on, e)}
         >
            <span className="toggleUI__track" aria-hidden="true">
               <span className="toggleUI__thumb" />
            </span>
         </button>
      </Root>
   );
});

export default ToggleUI;
